import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import io from 'socket.io-client';
import notificationService from '../services/notificationService';
import { useNotification } from './NotificationContext';

const InAppNotificationContext = createContext(null);

export const InAppNotificationProvider = ({ children, user }) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const { notification: antNotification } = useNotification();

    const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    };

    const subscribeToPush = useCallback(async () => {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            console.warn('Push notifications are not supported in this browser');
            return;
        }

        try {
            const registration = await navigator.serviceWorker.ready;
            
            // In a real app, this key should come from the server or env
            const publicVapidKey = process.env.REACT_APP_PUBLIC_VAPID_KEY || 'BF779L_R0n_v9S6X7_U_U_Y_u_V_W_X_Y_Z_0_1_2_3_4_5_6_7_8_9_A_B_C_D_E_F_G';
            
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicVapidKey)
            });

            await notificationService.subscribePush(subscription);
            console.log('Push subscription successful');
        } catch (error) {
            console.error('Failed to subscribe to push notifications', error);
        }
    }, []);

    const fetchNotifications = useCallback(async () => {
        if (!user) return;
        try {
            const data = await notificationService.getNotifications();
            setNotifications(data);
            const countData = await notificationService.getUnreadCount();
            setUnreadCount(countData.count);
        } catch (error) {
            console.error('Failed to fetch notifications', error);
        }
    }, [user]);

    useEffect(() => {
        if (user) {
            fetchNotifications();
            subscribeToPush();

            // Same origin; setupProxy.js (dev) / nginx (prod) forwards /socket.io to the backend.
            const socketUrl = process.env.REACT_APP_SOCKET_URL || window.location.origin;
            const newSocket = io(socketUrl, { path: '/socket.io' });

            newSocket.on('connect', () => {
                newSocket.emit('join', user.id);
            });

            newSocket.on('notification', (notification) => {
                setNotifications(prev => [notification, ...prev]);
                setUnreadCount(prev => prev + 1);
                
                // Show Ant Design notification
                antNotification.info({
                    message: notification.title,
                    description: notification.message,
                    placement: 'topRight',
                });
            });

            return () => newSocket.close();
        }
    }, [user, fetchNotifications, antNotification]);

    const markAsRead = async (id) => {
        try {
            await notificationService.markAsRead(id);
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Failed to mark notification as read', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await notificationService.markAllAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Failed to mark all notifications as read', error);
        }
    };

    return (
        <InAppNotificationContext.Provider value={{
            notifications,
            unreadCount,
            markAsRead,
            markAllAsRead,
            fetchNotifications,
            subscribeToPush
        }}>
            {children}
        </InAppNotificationContext.Provider>
    );
};

export const useInAppNotifications = () => {
    const context = useContext(InAppNotificationContext);
    if (!context) {
        throw new Error('useInAppNotifications must be used within an InAppNotificationProvider');
    }
    return context;
};
